import {
  DndContext,
  DragOverlay,
  closestCenter,
  pointerWithin,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronRight, FiEye, FiGrid } from 'react-icons/fi';
import { IoCloseOutline, IoLockClosed } from 'react-icons/io5';

import { Modal } from '@/components';
import { ButtonOutlinedSmall, ButtonSmall } from '@/components/Button';
import { Switch } from '@/components/Inputs';
import { getWidgetDef } from '@/utils/dashboard.registry';
import { CATEGORY_ICONS } from '@/utils/dashboard.types';
import type { WidgetCategory, WidgetId, WidgetLayoutItem } from '@/utils/dashboard.types';

import s from '../DashboardSettingsPage.module.scss';

type Platform = 'desktop' | 'mobile';

const GRID_COLS = 4;

function categoryTitle(category: WidgetCategory): string {
  let title = category
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  if (title === 'Vessel') title = 'Vessels';
  if (title === 'Media') title = 'Library';
  if (title === 'Service Provider') title = 'Service Providers';
  return title;
}

function computeRowCount(items: WidgetLayoutItem[]): number {
  let colsUsed = 0;
  let rows = 0;
  for (const item of items) {
    if (colsUsed === 0) rows++;
    if (colsUsed + item.col_span > GRID_COLS) {
      rows++;
      colsUsed = 0;
    }
    colsUsed += item.col_span;
    if (colsUsed >= GRID_COLS) colsUsed = 0;
  }
  return Math.max(rows, 4);
}

const collisionDetection: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  return hits.length > 0 ? hits : closestCenter(args);
};

type DashboardSettingsDesktopPageProps = {
  platform: Platform;
  setPlatform: (platform: Platform) => void;
  groupedWidgets: Record<WidgetCategory, WidgetLayoutItem[]>;
  lockedVisibleItems: WidgetLayoutItem[];
  sortableVisibleItems: WidgetLayoutItem[];
  sortableVisibleIds: string[];
  lockedIds: Set<WidgetId>;
  activeId: string | null;
  overlayWidth: number | null;
  gridRef: React.RefObject<HTMLDivElement | null>;
  sensors: ReturnType<typeof import('@dnd-kit/core').useSensors>;
  dirty: boolean;
  onToggle: (id: string) => void;
  onDragStart: (event: DragStartEvent) => void;
  onDragOver: (event: DragOverEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  loading: boolean;
  onCancel: () => void;
  onSave: () => void | Promise<void>;
};

function LockedPreviewCard({ item, platform }: { item: WidgetLayoutItem; platform: Platform }) {
  const def = getWidgetDef(item.id);
  if (!def) return null;

  const style = {
    gridColumn: platform === 'desktop' ? `span ${item.col_span}` : undefined,
    position: 'relative' as const,
  };

  return (
    <div style={style} className={`${s.previewCard} ${s.previewCardLocked}`}>
      <span className={s.previewCardLockIcon} aria-label="Locked">
        <IoLockClosed size={18} />
      </span>
      <div className={s.previewCardDragArea}>
        <span className={s.dragHandle}>{CATEGORY_ICONS[def.category]}</span>
        <span className={s.previewCardLabel}>{def.label}</span>
      </div>
    </div>
  );
}

function PreviewCardOverlay({
  item,
  platform,
  width,
}: {
  item: WidgetLayoutItem;
  platform: Platform;
  width: number | null;
}) {
  const def = getWidgetDef(item.id);
  if (!def) return null;

  const style = {
    width: width != null ? width : undefined,
  };

  return (
    <div
      style={style}
      className={`${s.previewCardOverlay} ${platform === 'mobile' ? s.previewCardOverlayMobile : ''}`}
    >
      <div className={s.previewCardDragArea}>
        <span className={s.dragHandle}>{CATEGORY_ICONS[def.category]}</span>
        <span className={s.previewCardLabel}>{def.label}</span>
      </div>
    </div>
  );
}

function SortablePreviewCard({
  item,
  platform,
  onRemove,
}: {
  item: WidgetLayoutItem;
  platform: Platform;
  onRemove: (id: string) => void;
}) {
  const def = getWidgetDef(item.id);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    gridColumn: platform === 'desktop' ? `span ${item.col_span}` : undefined,
    position: 'relative' as const,
  };

  if (!def) return null;

  if (isDragging) {
    return <div ref={setNodeRef} style={style} className={s.previewCardPlaceholder} />;
  }

  return (
    <div ref={setNodeRef} style={style} className={s.previewCard}>
      <button
        type="button"
        className={s.removeButton}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => onRemove(item.id)}
        aria-label={`Remove ${def.label} from Dashboard`}
        title="Remove"
      >
        <IoCloseOutline size={20} />
      </button>
      <div className={s.previewCardDragArea} {...attributes} {...listeners}>
        <span className={s.dragHandle}>{CATEGORY_ICONS[def.category]}</span>
        <span className={s.previewCardLabel}>{def.label}</span>
      </div>
    </div>
  );
}

export function DashboardSettingsDesktopPage({
  platform,
  setPlatform,
  groupedWidgets,
  lockedVisibleItems,
  sortableVisibleItems,
  sortableVisibleIds,
  lockedIds,
  activeId,
  overlayWidth,
  gridRef,
  sensors,
  dirty,
  onToggle,
  onDragStart,
  onDragOver,
  onDragEnd,
  loading,
  onCancel,
  onSave,
}: DashboardSettingsDesktopPageProps) {
  const [showPreview, setShowPreview] = useState(false);

  const allVisibleItems = [...lockedVisibleItems, ...sortableVisibleItems];

  const sectionRows = useMemo(
    () =>
      (Object.entries(groupedWidgets) as [WidgetCategory, WidgetLayoutItem[]][]).flatMap(
        ([category, items]) =>
          [...items].sort((a, b) => a.order - b.order).map((item) => ({ item, category })),
      ),
    [groupedWidgets],
  );

  const shownCount = sectionRows.filter((row) => row.item.visible).length;
  const totalCount = sectionRows.length;

  const closePreview = () => setShowPreview(false);

  return (
    <div className={s.embeddedPanel}>
      <header className={s.detailHeader}>
        <div className={s.detailIcon}>
          <FiGrid />
        </div>
        <div>
          <h2 className={s.detailTitle}>Dashboard</h2>
          <p className={s.detailSub}>Please choose which sections appear on your dashboard.</p>
        </div>
      </header>

      <div className={s.segmented}>
        <button
          type="button"
          className={`${s.seg} ${platform === 'desktop' ? s.segActive : ''}`}
          onClick={() => setPlatform('desktop')}
        >
          Desktop
        </button>
        <button
          type="button"
          className={`${s.seg} ${platform === 'mobile' ? s.segActive : ''}`}
          onClick={() => setPlatform('mobile')}
        >
          Mobile
        </button>
      </div>

      <button type="button" className={s.previewLink} onClick={() => setShowPreview(true)}>
        <FiEye />
        <span className={s.previewLinkText}>Preview & Arrange Layout</span>
        {dirty && <span className={s.countChip}>Unsaved</span>}
        <FiChevronRight className={s.navChevron} />
      </button>

      <p className={s.groupLabel}>
        Sections · {shownCount}/{totalCount} shown
      </p>
      <div className={s.group}>
        {sectionRows.map(({ item, category }) => {
          const def = getWidgetDef(item.id);
          if (!def) return null;
          const isLocked = lockedIds.has(item.id);
          return (
            <div key={item.id} className={s.row}>
              <div className={s.rowIcon}>{CATEGORY_ICONS[category]}</div>
              <div className={s.rowText}>
                <div className={s.rowTitle}>{def.label}</div>
                <div className={s.rowSub}>{categoryTitle(category)}</div>
              </div>
              {isLocked ? (
                <span className={s.lockBadge} aria-label="Always Shown">
                  <IoLockClosed size={16} />
                </span>
              ) : (
                <Switch checked={item.visible} onChange={() => onToggle(item.id)} />
              )}
            </div>
          );
        })}
      </div>

      <Modal open={showPreview} onClose={closePreview}>
        <div className={s.previewModalBody}>
          <div className={s.modalPreviewHeader}>
            <div>
              <div className={s.modalPreviewTitle}>
                {platform === 'desktop' ? 'Desktop' : 'Mobile'} preview
              </div>
              <div className={s.previewCaption}>Please drag the blocks to reorder them.</div>
            </div>
            <div className={s.previewActions}>
              <ButtonOutlinedSmall
                onClick={() => {
                  onCancel();
                  closePreview();
                }}
                disabled={loading}
              >
                Cancel
              </ButtonOutlinedSmall>
              <ButtonSmall onClick={onSave} disabled={loading}>
                {loading ? 'Saving…' : 'Save Changes'}
              </ButtonSmall>
            </div>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            autoScroll={false}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
          >
            <div className={s.previewGridWrapper}>
              <div
                className={`${s.gridBackground} ${platform === 'mobile' ? s.gridBackgroundMobile : ''}`}
              >
                {platform === 'desktop' &&
                  Array.from({ length: computeRowCount(allVisibleItems) * GRID_COLS }, (_, i) => (
                    <div key={i} className={s.gridCell} />
                  ))}
              </div>
              <div
                ref={gridRef}
                className={`${s.previewGrid} ${platform === 'mobile' ? s.previewGridMobile : ''}`}
              >
                {lockedVisibleItems.map((item) => (
                  <LockedPreviewCard key={item.id} item={item} platform={platform} />
                ))}
                <SortableContext items={sortableVisibleIds} strategy={rectSortingStrategy}>
                  {sortableVisibleItems.map((item) => (
                    <SortablePreviewCard
                      key={item.id}
                      item={item}
                      platform={platform}
                      onRemove={onToggle}
                    />
                  ))}
                </SortableContext>
              </div>
            </div>
            {createPortal(
              // Portal to the body so the fixed-positioned overlay is measured
              // against the viewport, not the modal (its backdrop-filter would
              // otherwise become the containing block and offset the overlay).
              <DragOverlay zIndex={10000}>
                {activeId ? (
                  <PreviewCardOverlay
                    item={sortableVisibleItems.find((w) => w.id === activeId)!}
                    platform={platform}
                    width={overlayWidth}
                  />
                ) : null}
              </DragOverlay>,
              document.body,
            )}
          </DndContext>
        </div>
      </Modal>
    </div>
  );
}
